import * as Yup from 'yup';
import endsWith from 'lodash/endsWith';
import TextField from '@mui/material/TextField';
import Select from 'src/components/Select';
import AutoCompleteWithFreeText from 'src/components/AutoComplete/AutoCompleteWithFreeText';
import {
  PEP_EMAIL_DOMAIN_PEPSICO,
  PEP_EMAIL_DOMAIN_PEPSICORPTST,
  PEP_EMAIL_DOMAIN_CORPPEPTST,
} from 'src/constants';
import { compact } from 'lodash';
import RadioButtonGroup from 'src/components/RadioButtonGroup';
import CheckboxGroup from 'src/components/CheckboxGroup';
import {
  SolutionGroupItemField,
  SolutionType,
  SolutionUserItemField,
} from '../types';
import {
  SOLUTION_USER_ROLE_OPTIONS,
  SOLUTION_USER_COUNTRY_CODE,
  NOTIFICATION_PREFERENCE_OPTIONS,
  IOTH_NOTIFICATION_PREFERENCE_OPTIONS,
} from './constants';

const solutionUserRoleField = {
  name: SolutionUserItemField.Role,
  label: 'Role',
  placeholder: 'Select a role',
  Component: Select,
  options: SOLUTION_USER_ROLE_OPTIONS,
  validation: Yup.string().required('User role is required'),
  fullWidth: true,
  required: true,
  sx: { width: ({ spacing }) => spacing(56) },
  description:
    'Select the appropriate role for the user. A solution owner has one additional access over solution user that is to the Users tab.',
};

const notificationPreferenceField = (solutionType) => ({
  name: SolutionUserItemField.NotificationPreference,
  label: 'Notification preference:',
  Component: CheckboxGroup,
  type: 'checkbox-group',
  required: false,
  labelStyle: { mr: 3 },
  options:
    solutionType === SolutionType.ioTH
      ? IOTH_NOTIFICATION_PREFERENCE_OPTIONS
      : NOTIFICATION_PREFERENCE_OPTIONS,
});

const solutionUserPhoneNumberField = [
  {
    name: SolutionUserItemField.CountryCode,
    label: 'Country Code',
    placeholder: 'Select Country Code',
    initialValue: '+1',
    options: SOLUTION_USER_COUNTRY_CODE,
    Component: Select,
    sx: { width: ({ spacing }) => spacing(17) },
  },
  {
    name: SolutionUserItemField.MobileNumber,
    label: 'Number',
    inputProps: {
      maxLength: 10,
    },
    onChange: (event, context) => {
      context.setValue(
        SolutionUserItemField.MobileNumber,
        event.target.value.replace(/[^0-9]+/g, '')
      );
    },
    validation: Yup.string().test(
      'empty-or-10-number-check',
      'Phone Number must be 10 characters',
      (value) => !value || value.length === 10
    ),
    placeholder: 'Enter phone number',
    Component: TextField,
    sx: { flexGrow: 1 },
  },
];

export const solutionUserAssignFormFields = (
  solutionType,
  options?: any,
  optionsLoading?: boolean,
  dataOptions?: any,
  dataLoading?: boolean
) =>
  compact([
    {
      name: SolutionUserItemField.Email,
      label: 'Email address',
      placeholder: 'Enter email address',
      Component: TextField,
      options: SOLUTION_USER_ROLE_OPTIONS,
      validation: Yup.string()
        .required('Email is required')
        .email('Must be a valid email')
        .test(
          'allowed-Domain',
          `Email should belong to the domain ${PEP_EMAIL_DOMAIN_PEPSICO}, ${PEP_EMAIL_DOMAIN_PEPSICORPTST} or ${PEP_EMAIL_DOMAIN_CORPPEPTST}`,
          (value) =>
            endsWith(value, `@${PEP_EMAIL_DOMAIN_PEPSICO}`) ||
            endsWith(value, `@${PEP_EMAIL_DOMAIN_PEPSICORPTST}`) ||
            endsWith(value, `@${PEP_EMAIL_DOMAIN_CORPPEPTST}`)
        ),
      fullWidth: true,
      required: true,
      initialValue: `@${PEP_EMAIL_DOMAIN_PEPSICO}`,
      description:
        'Enter the email address of a user who will be able to manage this solution.',
    },
    solutionUserRoleField,
    {
      name: 'label',
      label: 'Phone Number',
      Component: RadioButtonGroup,
      option: [],
      sx: { width: ({ spacing }) => spacing(56) },
    },
    ...solutionUserPhoneNumberField,
    notificationPreferenceField(solutionType),
    {
      name: SolutionUserItemField.GroupIds,
      label: 'Groups',
      options: dataOptions,
      Component: AutoCompleteWithFreeText,
      multiple: true,
      loading: dataLoading,
      sx: { width: ({ spacing }) => spacing(56) },
    },
    solutionType === SolutionType.Industrial && {
      name: SolutionUserItemField.SiteIds,
      label: 'Sites',
      options,
      Component: AutoCompleteWithFreeText,
      multiple: true,
      loading: optionsLoading,
      sx: { width: ({ spacing }) => spacing(56) },
    },
  ]);

export const solutionUserEditFormFields = (
  solutionType,
  options?: any,
  optionsLoading?: boolean,
  dataOptions?: any,
  dataLoading?: boolean,
  selectedGroupIds?: string[],
  selectedSiteIds?: string[]
) =>
  compact([
    {
      name: SolutionUserItemField.Email,
    },
    solutionUserRoleField,
    {
      name: 'label',
      label: 'Phone Number',
      Component: RadioButtonGroup,
      option: [],
      sx: { width: ({ spacing }) => spacing(56) },
    },
    ...solutionUserPhoneNumberField,
    notificationPreferenceField(solutionType),
    {
      name: SolutionUserItemField.GroupIds,
      label: 'Groups',
      options: dataOptions,
      Component: AutoCompleteWithFreeText,
      value: selectedGroupIds,
      multiple: true,
      loading: dataLoading,
      sx: { width: ({ spacing }) => spacing(56) },
    },
    solutionType === SolutionType.Industrial && {
      name: SolutionUserItemField.SiteIds,
      label: 'Sites',
      options,
      Component: AutoCompleteWithFreeText,
      value: selectedSiteIds,
      multiple: true,
      loading: optionsLoading,
      sx: { width: ({ spacing }) => spacing(56) },
    },
  ]);

export const solutionGroupsFormFields = () =>
  compact([
    {
      name: SolutionGroupItemField.Name,
      label: 'Name',
      Component: TextField,
      placeholder: 'Enter group name',
      required: true,
      validation: Yup.string().required('Group name is required'),
      sx: { width: ({ spacing }) => spacing(56) },
    },
    {
      name: SolutionGroupItemField.Description,
      label: 'Description',
      Component: TextField,
      placeholder: 'Enter description',
      multiline: true,
      rows: 3,
      sx: { width: ({ spacing }) => spacing(56) },
    },
  ]);
